// packages/app-shell/src/process/ProcessMinimap.tsx
//
// Bottom-right overview for the horizontal process views (BPMN + flow). A wide
// ribbon (MegaOrderSaga = 27 columns) runs far past the viewport; this shows
// the whole diagram shrunk to a strip with a viewport rectangle you can click
// to jump. It reads the scroll container's client size for the viewport rect
// (viewport chrome — NOT diagram layout geometry, which stays in the scene
// pass), and re-renders on scroll via the `scroll` prop the host already
// tracks. Nodes collapse to dots keyed only by grid position + kind, so the
// overview reads as the real diagram at a glance.

import { useCallback } from "react";
import type { RefObject } from "react";

/** A minimal, backend-agnostic node projection — grid position is enough. */
export interface MinimapDot {
  readonly x: number;
  readonly y: number;
  readonly round?: boolean;
}

interface ProcessMinimapProps {
  /** Total content size in px (the body SVG's width/height). */
  readonly contentW: number;
  readonly contentH: number;
  /** Grid → px mapping for the dots (scene CW/CH/PAD). */
  readonly cellW: number;
  readonly cellH: number;
  readonly pad: number;
  readonly headroom: number;
  readonly dots: readonly MinimapDot[];
  /** The scroll container to pan on click. */
  readonly scrollRef: RefObject<HTMLDivElement | null>;
  /** Latest scroll offset (host-tracked) — drives the viewport rect. */
  readonly scroll: { top: number; left: number };
  /** Viewport (scroll container client) size — host-tracked via ResizeObserver. */
  readonly viewW: number;
  readonly viewH: number;
}

const MAP_W = 168;
const MAP_MAX_H = 120;

export function ProcessMinimap({
  contentW,
  contentH,
  cellW,
  cellH,
  pad,
  headroom,
  dots,
  scrollRef,
  scroll,
  viewW,
  viewH,
}: ProcessMinimapProps) {
  // Only worth showing when the content overflows the viewport.
  const overflows = viewW > 0 && (contentW > viewW + 1 || contentH > viewH + 1);

  const scale = Math.min(MAP_W / contentW, MAP_MAX_H / contentH);
  const mapW = contentW * scale;
  const mapH = contentH * scale;

  const jump = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const node = scrollRef.current;
      if (!node) return;
      const r = e.currentTarget.getBoundingClientRect();
      // Centre the viewport on the clicked point.
      const cx = (e.clientX - r.left) / scale;
      const cy = (e.clientY - r.top) / scale;
      node.scrollTo({
        left: cx - node.clientWidth / 2,
        top: cy - node.clientHeight / 2,
        behavior: "smooth",
      });
    },
    [scrollRef, scale],
  );

  if (!overflows || !isFinite(scale) || scale <= 0) return null;

  return (
    <svg
      width={mapW}
      height={mapH}
      className="absolute bottom-2 right-2 cursor-pointer rounded border border-slate-700 bg-slate-900/80 shadow-lg"
      data-testid="process-minimap"
      onClick={jump}
      role="img"
      aria-label="Diagram overview"
    >
      {dots.map((d, i) => {
        const px = (pad + d.x * cellW + cellW / 2) * scale;
        const py = (pad + headroom + d.y * cellH + cellH / 2) * scale;
        const r = Math.max(1, cellH * scale * 0.28);
        return d.round ? (
          <circle key={i} cx={px} cy={py} r={r} className="fill-slate-500" />
        ) : (
          <rect key={i} x={px - r} y={py - r} width={r * 2} height={r * 2} rx={0.5} className="fill-slate-500" />
        );
      })}
      {/* Viewport rectangle. */}
      <rect
        x={scroll.left * scale}
        y={scroll.top * scale}
        width={Math.min(viewW, contentW) * scale}
        height={Math.min(viewH, contentH) * scale}
        className="fill-sky-400/15 stroke-sky-400"
        strokeWidth={1}
        data-testid="minimap-viewport"
      />
    </svg>
  );
}
